import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { CircularProgress, Button } from '@mui/material';
import { FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchParticipationPerformance } from 'ServerAPI/serverAPI';
import { handleCodeReview } from './codeReviewUtils';
import CodeReviewDisplay from './CodeReviewDisplay';

export const CodeModal = ({ show, handleClose, content }) => {
    const [codeData, setCodeData] = useState(null);
    const [rawUserData, setRawUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);

    useEffect(() => {
        if (show && content) {
            fetchCodeData();
        }
    }, [show, content]);

    const fetchCodeData = async () => {
        setLoading(true);
        try {
            const payload = {
                courseId: content.courseId || '',
                contentId: content.contentId || '',
                userId: content.userId || '',
                type: 'codeProgress'
            };
            console.log('Fetching code data with payload:', payload);
            const response = await fetchParticipationPerformance(payload);
            console.log('fetchParticipationPerformance response:', response);

            if (response?.status === 200 && response.data?.userData && Array.isArray(response.data.userData) && response.data.userData.length > 0) {
                const data = response.data.userData[0];
                setRawUserData(data);
                setCodeData({
                    contentId: data.contentId,
                    contentType: data.contentType,
                    completed: data.completed,
                    codeContent: data.codeSnippet || '',
                    problemStatement: data.problemStatement || 'No problem statement provided',
                    output: data.output || [],
                    language: data.language || ''
                });
                toast.success('Code data fetched successfully');
            } else {
                console.error('Failed to fetch code data:', {
                    status: response?.status,
                    data: response?.data,
                    message: response?.data?.message || 'Invalid response structure'
                });
                toast.error('Failed to fetch code data: Invalid response structure');
                setCodeData(null);
                setRawUserData(null);
            }
        } catch (error) {
            console.error('Error fetching code data:', error);
            toast.error('Error fetching code data: ' + (error.message || 'Unknown error'));
            setCodeData(null);
            setRawUserData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleReviewCode = async () => {
        if (!codeData || !codeData.codeContent || codeData.codeContent.trim() === '') {
            toast.warn('No code available to review!');
            return;
        }
        setReviewLoading(true);
        try {
            const payload = {
                language: codeData.language.toLowerCase() || 'python', // Use language from response
                files: [{ name: `user_code.${codeData.language.toLowerCase() === 'python' ? 'py' : 'sql'}`, content: codeData.codeContent.trim() }],
                contentId: codeData.contentId || content.contentId || '',
                courseId: content.courseId || '',
                sectionId: content.sectionId || '',
                userId: content.userId || '',
                version: '15'
            };
            console.log('Code review payload:', payload);
            await handleCodeReview(payload);
            toast.success('Code review PDF downloaded successfully!');
        } catch (err) {
            console.error('Error in handleReviewCode:', err);
            toast.error(`Failed to generate code review PDF: ${err.message || 'Unknown error'}`);
        } finally {
            setReviewLoading(false);
        }
    };

    const handleModalClose = () => {
        setCodeData(null);
        setRawUserData(null);
        handleClose();
    };

    // Loading: plain overlay — no Bootstrap Modal so no stale backdrop issues
    if (show && loading) {
        return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 30000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: 'white', padding: '40px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <CircularProgress />
                    <span style={{ color: '#374151', fontSize: '0.9rem' }}>Loading...</span>
                </div>
            </div>
        );
    }

    // Proctored submission: use full-screen CodeReviewDisplay
    // key={contentId} ensures fresh state for each submission
    if (show && rawUserData?.codeProctoringData) {
        return (
            <CodeReviewDisplay
                key={rawUserData?.contentId}
                show={true}
                handleClose={handleModalClose}
                userData={rawUserData}
                courseId={content?.courseId}
                userId={content?.userId}
                sectionId={content?.sectionId}
            />
        );
    }

    return (
        <Modal show={show} onHide={handleModalClose} size="xl" >
            <Modal.Header closeButton style={{ background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                <Modal.Title>Code Submission Review - {content?.contentTitle || 'N/A'}</Modal.Title>
                {codeData?.language === 'Python' && (
                    <Button
                        style={{
                            marginLeft: '20px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: 'white',
                            fontWeight: 600,
                            borderRadius: '12px',
                            padding: '8px 16px',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            cursor: reviewLoading ? 'not-allowed' : 'pointer'
                        }}
                        onClick={handleReviewCode}
                        disabled={reviewLoading || !codeData || !codeData.codeContent}
                    >
                        {reviewLoading ? <CircularProgress size={20} color="inherit" /> : <FileText size={20} />}
                        Review Code
                    </Button>
                )}
            </Modal.Header>
            <Modal.Body style={{ padding: '20px', height: '75vh', overflow: 'hidden' }}>
                {loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <CircularProgress />
                    </div>
                ) : codeData ? (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                            padding: '15px',
                            background: '#e3f2fd',
                            borderRadius: '8px',
                            marginBottom: '15px',
                            maxHeight: '150px',
                            overflowY: 'auto'
                        }}>
                            <h5 style={{ color: '#1976d2', marginBottom: '10px' }}>Problem Statement</h5>
                            <p style={{ margin: 0, lineHeight: '1.5' }}>{codeData.problemStatement}</p>
                        </div>

                        <div style={{ display: 'flex', flex: 1, gap: '15px' }}>
                            <div style={{ flex: '2', display: 'flex', flexDirection: 'column' }}>
                                <div style={{
                                    background: '#000000ff',
                                    color: 'white',
                                    padding: '10px',
                                    borderTopLeftRadius: '8px',
                                    borderTopRightRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}>
                                    Code
                                </div>
                                <pre style={{
                                    flex: 1,
                                    border: '1px solid #ccc',
                                    borderTop: 'none',
                                    borderBottomLeftRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                    padding: '15px',
                                    fontFamily: '"Fira Code", "Courier New", monospace',
                                    fontSize: '14px',
                                    backgroundColor: '#2d2a2aff',
                                    color: '#fff',
                                    margin: 0,
                                    overflowY: 'auto'
                                }}>
                                    <code>{codeData.codeContent}</code>
                                </pre>
                            </div>

                            <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                                <div style={{
                                    background: '#1e1e1e',
                                    color: 'white',
                                    padding: '8px 10px',
                                    borderTopLeftRadius: '8px',
                                    borderTopRightRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}>
                                    Output
                                </div>
                                <div style={{
                                    border: '1px solid #ccc',
                                    borderTop: 'none',
                                    borderBottomLeftRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                    padding: '10px',
                                    background: '#f9f9f9',
                                    fontSize: '12px',
                                    fontFamily: 'monospace',
                                    minHeight: '100px',
                                    flex: 1,
                                    overflowY: 'auto'
                                }}>
                                    {codeData.output && codeData.output.length > 0 ? (
                                        codeData.output.map((line, index) => (
                                            <pre key={index} style={{ margin: 0 }}>{line}</pre>
                                        ))
                                    ) : (
                                        <div>No output available</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        No code data available
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};